/**
 * API Service Wrappers - Dynamic Proxy Implementation
 *
 * This file provides a dynamic proxy-based wrapper system that maintains the old API
 * instantiation pattern while delegating to the new static service classes generated
 * by @hey-api/openapi-ts.
 *
 * This approach ensures backward compatibility while allowing the frontend to use
 * a consistent API pattern regardless of the underlying generator implementation.
 *
 * The dynamic proxy automatically wraps ALL methods from any service class, eliminating
 * the need for manual wrapper implementation and maintenance.
 */

import type { CancelablePromise } from '../../client'
import {
  AdministrationService,
  type ActiveBrandBody,
  type AIPromptConfigsPublic,
  AiclaimsService,
  AiPromptConfigsService,
  AnniesService,
  ApiBillingService,
  type ApiKeysMetadataPublic,
  type AppSettingPublic,
  type AppSettingsPublic,
  AuthoritiesService,
  type Circs_GradesPublic,
  CircsGradesService,
  type ClaimSummaryPublic,
  ClaimsService,
  type ComplaintPublic,
  type ComplaintsPublic,
  ComplaintsService,
  type InsurersPublic,
  InsurersService,
  InvoicesService,
  LoginService,
  type Message,
  OtpService,
  type Partner_RulePublic,
  type Partner_RulesPublic,
  PartnerRulesService,
  type PartnersPublic,
  PartnersService,
  RateLimitingService,
  ReferralAllocationsService,
  ReferralNotesService,
  ReferralsService,
  RepairBillingService,
  type ReportTemplateCreate,
  type ReportTemplatePublic,
  type ReportTemplatesPublic,
  ReportTemplatesService,
  type ReportTemplateUpdate,
  RequestsService,
  RoleAllocationsService,
  RolePermissionsService,
  RolesService,
  SchedulerService,
  SecurityTestsService,
  SettingsService,
  type Source_RatesPublic,
  SourceRatesService,
  SummariesService,
  type Supplier_RatesPublic,
  SupplierRatesService,
  type TaskScheduleConfigPublic,
  type TaskScheduleConfigsPublic,
  type TaskScheduleExecutionsPublic,
  UploadsService,
  UserPartnersService,
  type UserPublic,
  type UsersPublic,
  UsersService,
  type Veh_GroupsPublic,
  VehGroupsService,
  type WorkflowRateConfigCreate,
  type WorkflowRateConfigPublic,
  type WorkflowRateConfigUpdate,
  type WorkflowRateConfigsPublic,
  XeroService,
} from '../../client'
import { OpenAPI } from '../../client/core/OpenAPI'
import { request as __request } from '../../client/core/request'

export interface ClaimSummaryRecentRunRecord {
  id: number
  referralId: number
  sourceRef?: string | null
  hireStatus?: string | null
  taskId?: string | null
  userId?: number | null
  tenantId?: string | null
  workflowId?: number | null
  version: string
  status: string
  summaryExcerpt?: string | null
  errorMessage?: string | null
  generationTime?: number | null
  cacheHit: boolean
  modelVersion?: string | null
  createdDatetime: string
  updatedDatetime?: string | null
  feedbackRating?: string | null
}

export interface ClaimSummaryRecentRunsResponse {
  data: Array<ClaimSummaryRecentRunRecord>
  count: number
}

export interface ReportTemplatePreviewResponse {
  html: string
}

/** Workflow billing ledger summary (admin API billing page). */
export interface WorkflowBillingLedgerSummary {
  partner_id: number
  billing_period: string
  total_runs: number
  pending_runs: number
  billed_runs: number
  excluded_runs: number
  capped_runs: number
  evaluation_runs: number
}

/** One row from GET /api-billing/workflow-ledger/events */
export interface WorkflowBillingLedgerEventRow {
  id: number
  partner_id?: number | null
  workflow_key: string
  referral_id?: number | null
  task_id?: string | null
  billing_type: string
  billing_period: string
  status: string
  invoice_id?: number | null
  started_at: string
  evaluation_mode: boolean
  created_datetime?: string
  updated_datetime?: string | null
}

export type AiClaimsDashboardSummary =
  import('../../client').AiClaimsDashboardSummaryPublic
export type AiClaimsWorkflowCard = import('../../client').WorkflowCardPublic
export interface AiClaimsWorkflowCardsResponse {
  data: Array<AiClaimsWorkflowCard>
}

/**
 * Normalize errors to maintain consistent error handling across all API calls
 */
function normalizeError(error: any): Error {
  // Preserve existing error structure that components expect
  if (error?.response?.detail) {
    const normalizedError = new Error(error.response.detail)
    ;(normalizedError as any).response = error.response
    return normalizedError
  }

  if (error?.message) {
    return error
  }

  return new Error('Unknown API error')
}

/**
 * Creates a dynamic API wrapper that automatically wraps all methods from a service class
 * with consistent error handling while maintaining the instantiable interface pattern.
 *
 * @param ServiceClass - The static service class to wrap
 * @returns A new class that can be instantiated and provides wrapped access to all service methods
 */
function createApiWrapper<T extends Record<string, any>>(
  ServiceClass: T,
  serviceName: string,
): new () => T & { [key: string]: any } {
  // Derive the prefix using the explicitly provided name to survive minification
  const prefix = serviceName.replace('Service', '')
  const camelCasePrefix = prefix.charAt(0).toLowerCase() + prefix.slice(1)

  return class ApiWrapper {
    constructor() {
      // Create a proxy that intercepts all property access
      // biome-ignore lint/correctness/noConstructorReturn: Proxy instance intentionally returned for API wrapper
      return new Proxy(this as unknown as T & { [key: string]: any }, {
        get(_target, prop: string | symbol) {
          // 1. Try exact match
          let serviceProp = ServiceClass[prop as keyof T]

          // 2. Try prefixed match (e.g., listTables -> administrationListTables)
          if (!serviceProp && typeof prop === 'string' && camelCasePrefix) {
            const prefixedProp =
              camelCasePrefix + prop.charAt(0).toUpperCase() + prop.slice(1)
            serviceProp = ServiceClass[prefixedProp as keyof T]
          }

          // If it's a function (method), wrap it with error handling
          if (typeof serviceProp === 'function') {
            return async (...args: any[]) => {
              try {
                return await serviceProp.apply(ServiceClass, args)
              } catch (error) {
                throw normalizeError(error)
              }
            }
          }

          // For non-function properties, return as-is
          return serviceProp
        },
      }) as T & { [key: string]: any }
    }
  } as new () => T & { [key: string]: any }
}

// Type definitions for bulk operation referrals
export interface BulkOperationReferralRow {
  referral_id: number
  claim_id: number | null
  client_firstname: string | null
  client_lastname: string | null
  client_vehicle_reg: string | null
}

export interface BulkOperationReferralsResponse {
  data: BulkOperationReferralRow[]
  count: number
}

export interface TDataGetBulkOperationReferrals {
  operationId: number
  skip?: number
  limit?: number
}

// Extended AdministrationService with generic database methods
class AdministrationServiceExtended extends AdministrationService {
  public static listTables(): CancelablePromise<string[]> {
    return AdministrationService.listTables()
  }

  public static readTableRows(data: {
    tableName: string
    skip?: number
    limit?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    search?: string
  }): CancelablePromise<Record<string, any>> {
    const { tableName, skip, limit, sortBy, sortOrder, search } = data
    return AdministrationService.readTableRows({
      tableName,
      skip,
      limit,
      sortBy,
      sortOrder,
      search,
    } as any) as CancelablePromise<Record<string, any>>
  }

  public static updateTableId(data: {
    tableName: string
    rowId: any
    requestBody: Record<string, any>
  }): CancelablePromise<Record<string, any>> {
    const { tableName, rowId, requestBody } = data
    return AdministrationService.updateTableRow({
      tableName,
      rowId,
      requestBody,
    }) as CancelablePromise<Record<string, any>>
  }

  public static deleteTableId(data: {
    tableName: string
    rowId: any
  }): CancelablePromise<Record<string, any>> {
    const { tableName, rowId } = data
    return AdministrationService.deleteTableRow({
      tableName,
      rowId,
    }) as CancelablePromise<Record<string, any>>
  }

  // Rate Limit methods (Fixing naming mismatches for build)
  public static deleteRateLimitConfigEndpoint(
    data: any,
  ): CancelablePromise<any> {
    return AdministrationService.deleteRateLimitConfigEndpoint(data)
  }

  public static updateRateLimitConfigEndpoint(
    data: any,
  ): CancelablePromise<any> {
    return AdministrationService.updateRateLimitConfigEndpoint(data)
  }

  public static createRateLimitConfigEndpoint(
    data: any,
  ): CancelablePromise<any> {
    return AdministrationService.createRateLimitConfigEndpoint(data)
  }
}

// Extended ReferralsService with bulk operation referrals method
class ReferralsServiceExtended extends ReferralsService {
  public static getBulkOperationReferrals(
    data: TDataGetBulkOperationReferrals,
  ): CancelablePromise<Record<string, unknown>> {
    return ReferralsService.getBulkOperationReferrals(
      data,
    ) as CancelablePromise<Record<string, unknown>>
  }
}

// Extended AiPromptConfigsService with legacy method names
class AiPromptConfigsServiceExtended extends AiPromptConfigsService {
  public static listAiPromptConfigs(): CancelablePromise<AIPromptConfigsPublic> {
    return AiPromptConfigsService.listAiPromptConfigs()
  }

  public static listPromptSlots(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/ai-prompt-configs/slots',
    })
  }

  public static getPromptHistory(data: {
    model: string
    variant?: string
    agent_key?: string | null
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/ai-prompt-configs/history',
      query: data,
    })
  }

  public static createPromptDraft(data: {
    requestBody: Record<string, unknown>
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/ai-prompt-configs/drafts',
      body: data.requestBody,
      mediaType: 'application/json',
    })
  }

  public static updatePromptDraft(data: {
    configId: number
    requestBody: Record<string, unknown>
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/v1/ai-prompt-configs/drafts/{config_id}',
      path: { config_id: data.configId },
      body: data.requestBody,
      mediaType: 'application/json',
    })
  }

  public static promotePromptDraft(data: { configId: number }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/ai-prompt-configs/drafts/{config_id}/promote',
      path: { config_id: data.configId },
    })
  }

  public static retirePromptConfig(data: { configId: number }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/ai-prompt-configs/{config_id}/retire',
      path: { config_id: data.configId },
    })
  }

  public static updatePromptConfig(data: any): CancelablePromise<any> {
    return AiPromptConfigsService.updatePromptConfig(data)
  }

  public static createPromptConfig(data: any): CancelablePromise<any> {
    return AiPromptConfigsService.createPromptConfig(data)
  }

  public static getAiPromptConfig(data: any): CancelablePromise<any> {
    return AiPromptConfigsService.getAiPromptConfig(data)
  }

  public static pullFromAgenta(data: {}): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/ai-prompt-configs/agenta-pull',
      body: data,
      mediaType: 'application/json',
    })
  }

  public static listAgentaApps(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/ai-prompt-configs/agenta-apps',
    })
  }
}

// Extended SchedulerService with legacy method names
class SchedulerServiceExtended extends SchedulerService {
  public static getRedbeatStatus(): CancelablePromise<Record<string, unknown>> {
    return SchedulerService.getRedbeatStatus()
  }

  public static getAvailableTasks(): CancelablePromise<
    Array<Record<string, unknown>>
  > {
    return SchedulerService.getAvailableTasks()
  }

  public static listTaskConfigs(
    data?: any,
  ): CancelablePromise<TaskScheduleConfigsPublic> {
    return SchedulerService.listTaskConfigs(data)
  }

  public static createTaskSchedule(
    data: any,
  ): CancelablePromise<TaskScheduleConfigPublic> {
    return SchedulerService.createTaskSchedule(data)
  }

  public static updateTaskSchedule(
    data: any,
  ): CancelablePromise<TaskScheduleConfigPublic> {
    return SchedulerService.updateTaskSchedule(data)
  }

  public static disableTaskSchedule(
    data: any,
  ): CancelablePromise<Record<string, unknown>> {
    return SchedulerService.deleteTaskSchedule(data)
  }

  public static triggerTaskNow(
    data: any,
  ): CancelablePromise<Record<string, unknown>> {
    return SchedulerService.triggerTaskNow(data)
  }

  public static triggerRedbeatSync(): CancelablePromise<
    Record<string, unknown>
  > {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/scheduler/redbeat-sync',
    })
  }

  public static getTaskExecutionHistory(
    data?: any,
  ): CancelablePromise<TaskScheduleExecutionsPublic> {
    return SchedulerService.getTaskExecutionHistory(data)
  }
}

// Extended SettingsService with legacy method names
class SettingsServiceExtended extends SettingsService {
  public static listAppSettings(
    data?: any,
  ): CancelablePromise<AppSettingsPublic> {
    return SettingsService.listAppSettings(data)
  }

  public static updateSetting(data: any): CancelablePromise<AppSettingPublic> {
    return SettingsService.updateSetting(data)
  }

  public static getAppSettingEndpoint(
    data: any,
  ): CancelablePromise<AppSettingPublic> {
    return SettingsService.getAppSettingEndpoint(data)
  }
}

// Privacy endpoint wrapper (OpenAPI client not yet regenerated for these routes)
class PrivacyServiceExtended {
  public static rehydratePrivacyToken(data: {
    scopeKey: string
    token: string
    justification: string
  }): CancelablePromise<{ found: boolean; resolvedValue?: string | null }> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/privacy/rehydrate',
      body: data,
      mediaType: 'application/json',
    })
  }

  public static purgeExpiredPrivacyMappings(): CancelablePromise<{
    deleted: number
  }> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/privacy/purge-expired',
    })
  }
}

// Extended CircsGradesService
class CircsGradesServiceExtended extends CircsGradesService {
  public static readCircsGrades(
    data?: any,
  ): CancelablePromise<Circs_GradesPublic> {
    return CircsGradesService.readCircsGrades(data)
  }
}

// Extended InsurersService
class InsurersServiceExtended extends InsurersService {
  public static readInsurers(data?: any): CancelablePromise<InsurersPublic> {
    return InsurersService.readInsurers(data)
  }
}

// Extended PartnerRulesService
class PartnerRulesServiceExtended extends PartnerRulesService {
  public static readPartnerRules(
    data?: any,
  ): CancelablePromise<Partner_RulesPublic> {
    return PartnerRulesService.readPartnerRules(data)
  }
  public static createPartnerRule(
    data: any,
  ): CancelablePromise<Partner_RulePublic> {
    return PartnerRulesService.createPartnerRule(data)
  }
}

// Extended PartnersService
class PartnersServiceExtended extends PartnersService {
  public static readPartners(data?: any): CancelablePromise<PartnersPublic> {
    return PartnersService.readPartners(data)
  }
}

// Extended SourceRatesService
class SourceRatesServiceExtended extends SourceRatesService {
  public static readSourceRates(
    data?: any,
  ): CancelablePromise<Source_RatesPublic> {
    return SourceRatesService.readSourceRates(data)
  }
  public static createSourceRate(data: any): CancelablePromise<Message> {
    return SourceRatesService.createSourceRate(data)
  }
  public static expireSourceRates(data: any): CancelablePromise<Message> {
    return SourceRatesService.expireSourceRates(data)
  }
}

// Extended VehGroupsService
class VehGroupsServiceExtended extends VehGroupsService {
  public static readVehGroups(data?: any): CancelablePromise<Veh_GroupsPublic> {
    return VehGroupsService.readVehGroups(data)
  }
}

// Extended ComplaintsService
class ComplaintsServiceExtended extends ComplaintsService {
  public static readComplaints(
    data?: any,
  ): CancelablePromise<ComplaintsPublic> {
    return ComplaintsService.readComplaints(data)
  }
  public static createNewComplaint(
    data: any,
  ): CancelablePromise<ComplaintPublic> {
    return ComplaintsService.createNewComplaint(data)
  }
  public static readComplaint(data: any): CancelablePromise<ComplaintPublic> {
    return ComplaintsService.readComplaint(data)
  }
  public static updateExistingComplaint(
    data: any,
  ): CancelablePromise<ComplaintPublic> {
    return ComplaintsService.updateExistingComplaint(data)
  }
  public static readComplaintsByReferral(
    data: any,
  ): CancelablePromise<ComplaintsPublic> {
    return ComplaintsService.readComplaintsByReferral(data)
  }
}

// Extended ReferralNotesService
class ReferralNotesServiceExtended extends ReferralNotesService {
  public static getNotes(data: any): CancelablePromise<any> {
    return ReferralNotesService.getNotes(data)
  }
  public static getNotificationOptions(data: any): CancelablePromise<any> {
    return ReferralNotesService.getNotificationOptions(data)
  }
  public static addNote(data: any): CancelablePromise<any> {
    return ReferralNotesService.addNote(data)
  }
}

// Extended SummariesService
class SummariesServiceExtended extends SummariesService {
  public static generateSummary(
    data: any,
  ): CancelablePromise<Record<string, unknown>> {
    return SummariesService.generateSummary(data)
  }
  public static getSummaryStatus(
    data: any,
  ): CancelablePromise<Record<string, unknown>> {
    return SummariesService.getSummaryStatus(data)
  }
  public static getLatestSummary(
    data: any,
  ): CancelablePromise<ClaimSummaryPublic | null> {
    return SummariesService.getLatestSummary(data)
  }
  public static getSummaryHistory(
    data: any,
  ): CancelablePromise<Array<ClaimSummaryPublic>> {
    return SummariesService.getSummaryHistory(data)
  }

  public static getRecentRuns(data?: {
    skip?: number
    limit?: number
    status?: string
    version?: string
    hireStatus?: string
    fromDate?: string
    toDate?: string
    workflowId?: number
  }): CancelablePromise<ClaimSummaryRecentRunsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/summaries/recent',
      query: {
        skip: data?.skip,
        limit: data?.limit,
        status: data?.status,
        version: data?.version,
        hireStatus: data?.hireStatus,
        fromDate: data?.fromDate,
        toDate: data?.toDate,
        workflowId: data?.workflowId,
      },
    })
  }

  public static submitSummaryFeedback(data: {
    referralId: number
    requestBody: {
      rating: string
      comment?: string | null
      reason_code?: string | null
      task_id?: string | null
    }
  }): CancelablePromise<Record<string, unknown>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/summaries/referral/{referral_id}/feedback',
      path: { referral_id: data.referralId },
      body: data.requestBody,
      mediaType: 'application/json',
    })
  }
}

// Extended RequestsService
class RequestsServiceExtended extends RequestsService {
  public static createRequest(data: any): CancelablePromise<unknown> {
    return RequestsService.createRequest(data)
  }
}

// Extended SupplierRatesService
class SupplierRatesServiceExtended extends SupplierRatesService {
  public static readSupplierRates(
    data?: any,
  ): CancelablePromise<Supplier_RatesPublic> {
    return SupplierRatesService.readSupplierRates(data)
  }
  public static createSupplierRate(data: any): CancelablePromise<Message> {
    return SupplierRatesService.createSupplierRate(data)
  }
  public static expireSupplierRates(data: any): CancelablePromise<Message> {
    return SupplierRatesService.expireSupplierRates(data)
  }
}

class UsersServiceExtended extends UsersService {
  public static usersSetActiveBrandMe(data: {
    requestBody: ActiveBrandBody
  }): CancelablePromise<unknown> {
    return UsersServiceExtended.setActiveBrandMe(data)
  }

  public static generateApiKeyForUserWithBody(data: {
    userId: number
    requestBody?: {
      name?: string
      scopes?: Record<string, string[] | string>
      expiresDatetime?: string | null
      partnerId?: number | null
    }
  }): CancelablePromise<{ key: string }> {
    return __request(OpenAPI, {
      method: 'POST',
      url: `/api/v1/users/${data.userId}/generate-api-key`,
      body: data.requestBody,
      mediaType: 'application/json',
    })
  }

  public static readServiceAccounts(
    data: { skip?: number; limit?: number } = {},
  ): CancelablePromise<UsersPublic> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/users/service-accounts',
      query: {
        skip: data.skip,
        limit: data.limit,
      },
    })
  }

  public static createServiceAccount(
    data: Parameters<typeof UsersService.createServiceAccount>[0],
  ): CancelablePromise<UserPublic> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/users/service-accounts',
      body: data.requestBody,
      mediaType: 'application/json',
    })
  }

  public static listApiKeysForUser(data: {
    userId: number
    skip?: number
    limit?: number
  }): CancelablePromise<ApiKeysMetadataPublic> {
    return __request(OpenAPI, {
      method: 'GET',
      url: `/api/v1/users/${data.userId}/api-keys`,
      query: {
        skip: data.skip,
        limit: data.limit,
      },
    })
  }

  public static updateApiKeyForUser(
    data: Parameters<typeof UsersService.updateApiKeyForUser>[0],
  ): ReturnType<typeof UsersService.updateApiKeyForUser> {
    return UsersService.updateApiKeyForUser(data)
  }
}

class AiclaimsServiceExtended extends AiclaimsService {
  public static getDashboardSummary(data?: {
    fromDate?: string | null
    toDate?: string | null
  }): CancelablePromise<AiClaimsDashboardSummary> {
    return AiclaimsServiceExtended.getAiclaimsDashboardSummary(data)
  }

  public static getWorkflowCards(data?: {
    fromDate?: string | null
    toDate?: string | null
  }): CancelablePromise<AiClaimsWorkflowCardsResponse> {
    return AiclaimsServiceExtended.getAiclaimsWorkflowCards(data)
  }

  public static getRecentRuns(data?: {
    skip?: number
    limit?: number
    status?: string | null
    version?: string | null
    hireStatus?: string | null
    fromDate?: string | null
    toDate?: string | null
    workflowId?: number | null
  }): CancelablePromise<ClaimSummaryRecentRunsResponse> {
    return SummariesService.getRecentSummaryRuns(
      data,
    ) as unknown as CancelablePromise<ClaimSummaryRecentRunsResponse>
  }
}

// OpenAPI generator types ledger summary as Record<string, unknown>; extended adds a typed wrapper.
// @ts-expect-error TS4114 — static signatures differ after openapi regeneration (narrow return vs Record).
class ApiBillingServiceExtended extends ApiBillingService {
  public static getPartnerWorkflowRates(data: {
    partnerId: number
  }): CancelablePromise<WorkflowRateConfigsPublic> {
    const { partnerId } = data
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/api-billing/workflow-rates/partner/{partner_id}',
      path: {
        partner_id: partnerId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  public static createWorkflowRateConfig(data: {
    requestBody: WorkflowRateConfigCreate
  }): CancelablePromise<WorkflowRateConfigPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/api-billing/workflow-rates',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    })
  }

  public static updateWorkflowRateConfig(data: {
    configId: number
    requestBody: WorkflowRateConfigUpdate
  }): CancelablePromise<WorkflowRateConfigPublic> {
    const { configId, requestBody } = data
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/v1/api-billing/workflow-rates/{config_id}',
      path: {
        config_id: configId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    })
  }

  public static deleteWorkflowRateConfig(data: {
    configId: number
  }): CancelablePromise<Record<string, string>> {
    const { configId } = data
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/v1/api-billing/workflow-rates/{config_id}',
      path: {
        config_id: configId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  public static getWorkflowBillingLedgerSummary(data: {
    partnerId: number
    billingPeriod: string
  }): CancelablePromise<WorkflowBillingLedgerSummary> {
    const { partnerId, billingPeriod } = data
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/api-billing/workflow-ledger/summary',
      query: {
        partner_id: partnerId,
        billing_period: billingPeriod,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  public static getWorkflowBillingLedgerEvents(data: {
    partnerId: number
    billingPeriod: string
    skip?: number
    limit?: number
    includeEvaluation?: boolean
  }): CancelablePromise<{
    data: WorkflowBillingLedgerEventRow[]
    count: number
  }> {
    const {
      partnerId,
      billingPeriod,
      skip = 0,
      limit = 50,
      includeEvaluation = false,
    } = data
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/api-billing/workflow-ledger/events',
      query: {
        partner_id: partnerId,
        billing_period: billingPeriod,
        skip,
        limit,
        includeEvaluation,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

// Export all API wrappers using the dynamic proxy approach
// These maintain the exact same interface as the original manual wrappers
// but automatically include ALL methods from each service class

export const AdministrationApi = createApiWrapper(
  AdministrationServiceExtended,
  'AdministrationService',
)
export const AiPromptConfigsApi = createApiWrapper(
  AiPromptConfigsServiceExtended,
  'AiPromptConfigsService',
)
export const AnniesApi = createApiWrapper(AnniesService, 'AnniesService')
export const ApiBillingApi = createApiWrapper(
  ApiBillingServiceExtended,
  'ApiBillingService',
)
export const AuthoritiesApi = createApiWrapper(
  AuthoritiesService,
  'AuthoritiesService',
)
export const CircsGradesApi = createApiWrapper(
  CircsGradesServiceExtended,
  'CircsGradesService',
)
export const ClaimsApi = createApiWrapper(ClaimsService, 'ClaimsService')
export const ComplaintsApi = createApiWrapper(
  ComplaintsServiceExtended,
  'ComplaintsService',
)
export const InsurersApi = createApiWrapper(
  InsurersServiceExtended,
  'InsurersService',
)
export const InvoicesApi = createApiWrapper(InvoicesService, 'InvoicesService')
export const LoginApi = createApiWrapper(LoginService, 'LoginService')
export const OtpApi = createApiWrapper(OtpService, 'OtpService')
export const PartnerRulesApi = createApiWrapper(
  PartnerRulesServiceExtended,
  'PartnerRulesService',
)
export const PartnersApi = createApiWrapper(
  PartnersServiceExtended,
  'PartnersService',
)
export const RateLimitingApi = createApiWrapper(
  RateLimitingService,
  'RateLimitingService',
)
export const ReferralAllocationsApi = createApiWrapper(
  ReferralAllocationsService,
  'ReferralAllocationsService',
)
export const ReferralNotesApi = createApiWrapper(
  ReferralNotesServiceExtended,
  'ReferralNotesService',
)

// Create a properly typed ReferralsApi that exposes all ReferralsService methods as instance methods
class ReferralsApiClass {
  // Delegate all ReferralsService static methods as instance methods
  readReferrals(data: any) {
    return ReferralsService.readReferrals(data)
  }
  createReferral(data: any) {
    return ReferralsService.createReferral(data)
  }
  readReferral(data: any) {
    return ReferralsService.readReferral(data)
  }
  updateReferral(data: any) {
    return ReferralsService.updateReferral(data)
  }
  deleteReferral(data: any) {
    return ReferralsService.deleteReferral(data)
  }
  referralTableData(data: any) {
    return ReferralsService.referralTableData(data)
  }
  referralChartData(data: any) {
    return ReferralsService.referralChartData(data)
  }
  getBulkOperationStatus(data: any) {
    return ReferralsService.getBulkOperationStatus(data)
  }
  getBulkOperationReferrals(data: any) {
    return ReferralsServiceExtended.getBulkOperationReferrals(data)
  }
  getBulkOperationsHistory(data: any) {
    return ReferralsService.getBulkOperationsHistory(data)
  }
  countReferralsByStatus(data: any) {
    return ReferralsService.countReferralsByStatus(data)
  }
  createBulkSummaryOperation(data: any) {
    return ReferralsService.createBulkSummaryOperation(data)
  }
}

export const ReferralsApi = ReferralsApiClass
export const RepairBillingApi = createApiWrapper(
  RepairBillingService,
  'RepairBillingService',
)
export const ReportTemplatesApi = createApiWrapper(
  ReportTemplatesService,
  'ReportTemplatesService',
)
export type {
  ReportTemplatePublic,
  ReportTemplatesPublic,
  ReportTemplateCreate,
  ReportTemplateUpdate,
}
export const RequestsApi = createApiWrapper(
  RequestsServiceExtended,
  'RequestsService',
)
export const RoleAllocationsApi = createApiWrapper(
  RoleAllocationsService,
  'RoleAllocationsService',
)
export const RolePermissionsApi = createApiWrapper(
  RolePermissionsService,
  'RolePermissionsService',
)
export const RolesApi = createApiWrapper(RolesService, 'RolesService')
export const SchedulerApi = createApiWrapper(
  SchedulerServiceExtended,
  'SchedulerService',
)
export const SettingsApi = createApiWrapper(
  SettingsServiceExtended,
  'SettingsService',
)
export const PrivacyApi = createApiWrapper(
  PrivacyServiceExtended,
  'PrivacyService',
)
export const SourceRatesApi = createApiWrapper(
  SourceRatesServiceExtended,
  'SourceRatesService',
)
export const SourceRatesApiLegacy = createApiWrapper(
  SourceRatesServiceExtended,
  'SourceRatesService',
)
export const SummariesApi = createApiWrapper(
  SummariesServiceExtended,
  'SummariesService',
)
export const SupplierRatesApi = createApiWrapper(
  SupplierRatesServiceExtended,
  'SupplierRatesService',
)
export const UploadsApi = createApiWrapper(UploadsService, 'UploadsService')
export const UserPartnersApi = createApiWrapper(
  UserPartnersService,
  'UserPartnersService',
)
export const UsersApi = createApiWrapper(UsersServiceExtended, 'UsersService')
export const AiClaimsApi = createApiWrapper(
  AiclaimsServiceExtended,
  'AiclaimsService',
)
export const SecurityTestsApi = createApiWrapper(
  SecurityTestsService,
  'SecurityTestsService',
)
export const VehGroupsApi = createApiWrapper(
  VehGroupsServiceExtended,
  'VehGroupsService',
)
export const XeroApi = createApiWrapper(XeroService, 'XeroService')

// Re-export all types from the client for convenience
export * from '../../client'
